<template>
  <div
    data-test="campsite-description"
    :class="{ 'basic-listing': !campsite.isBookable }"
  >
    <template v-if="campsite.description">
      <div class="site-details top">
        <site-types :site-types="campsite.siteTypes" />
        <div>
          <translate v-if="campsite.openAllYear" class="open-status">
            Open all year
          </translate>
          <translate
            v-if="!campsite.openAllYear && openNow"
            class="open-status"
          >
            Open now
          </translate>
          <campsite-next-open :campsite="campsite" />

          <div
            v-if="campsite.association && campsite.association.gradeStars > 0"
            class="tourist-board"
          >
            <translate tag="p"> Tourist board rating </translate>
            <div class="tourist-board-stars">
              <img
                v-for="(_, index) in campsite.association.gradeStars"
                :key="index"
                loading="lazy"
                class="star-icon"
                src="@/assets/images/rating-star.svg"
                alt=""
                width="21"
                height="20"
              /><img
                v-for="(_, index) in 5 - campsite.association.gradeStars"
                :key="'greyStar' + index"
                loading="lazy"
                class="star-icon"
                src="@/assets/images/rating-star-grey.svg"
                alt=""
                width="21"
                height="20"
              />
            </div>
          </div>
        </div>
      </div>
      <template v-if="$isDesktop">
        <template v-if="campsite.isBookable">
          <translate class="notices-title"> Please note </translate>
          <campsite-notices
            :warnings="campsite.warnings"
            :notices="campsite.notices"
            :notices-needs-translation="campsite.noticesNeedsTranslation"
            :campsite-notices="campsite.campsiteNotices"
          />
        </template>
      </template>
      <campsite-description-text
        v-else
        :description-needs-translation="campsite.descriptionNeedsTranslation"
        :description="campsite.description"
      />

      <div class="site-details bottom">
        <div class="languages-spoken">
          <translate tag="p"> Languages spoken </translate>
          <p
            v-for="(language, index) in campsite.languages"
            :key="index"
            class="language-spoken"
          >
            <img
              loading="lazy"
              class="tick-icon"
              src="@/assets/images/tick.svg"
              height="13"
              width="15"
              alt=""
            />
            {{ language.name }}
          </p>
        </div>

        <div v-if="!campsite.openAllYear" class="opening-dates">
          <p
            v-for="(openingDateRange, index) in campsite.openingDateRanges"
            :key="index"
          >
            <i18n-t keypath="Open from {openingDate} to {closingDate}">
              <template #openingDate>
                <pu-date
                  tag="strong"
                  :date="openingDateRange.openingDate"
                  options="noYear"
                />
                <pu-date
                  v-if="!isOpenAndCloseSameYear(openingDateRange)"
                  tag="span"
                  :date="openingDateRange.openingDate"
                  options="year"
                />
              </template>
              <template #closingDate>
                <strong>
                  <pu-date
                    :date="openingDateRange.closingDate"
                    options="noYear"
                  />
                </strong>
                <pu-date :date="openingDateRange.closingDate" options="year" />
              </template>
            </i18n-t>
          </p>
        </div>
      </div>

      <div
        v-if="campsite.group && campsite.group.name && !campsite.group.image"
        class="campsite-group"
      >
        <translate tag="p" :tt="{ groupName: campsite.group.name }">
          This site is a member of {groupName} group.
        </translate>
      </div>

      <div
        v-if="
          campsite.group ||
          (campsite.association &&
            campsite.association.aaPennantRating &&
            campsite.association.aaPennantRating !== null)
        "
        class="site-awards"
      >
        <img
          v-if="campsite.group && campsite.group.image"
          loading="lazy"
          :alt="`${campsite.group.name}`"
          :src="`${campsite.group.image}`"
          width="100"
          height="100"
        />
        <img
          v-if="
            campsite.association.aaPennantRating &&
            Number(campsite.association.aaPennantRating) > 0 &&
            Number(campsite.association.aaPennantRating) < 6
          "
          loading="lazy"
          :alt="`AA ${campsite.association.aaPennantRating} pennants`"
          :src="
            imageModules[
              `/assets/aa-pennant-rating/aa-${campsite.association.aaPennantRating}.png`
            ].default
          "
          width="72"
          height="96"
        />
        <img
          v-else-if="campsite.association.aaPennantRating !== null"
          loading="lazy"
          width="56"
          height="45"
          src="@/assets/aa-pennant-rating/aa-holiday-centre.png"
          alt="AA Holiday Centre"
        />
      </div>
      <template v-if="campsite.group">
        <pu-search-link
          :facet="campsite.group.slug"
          :modify-filters="{ hierarchyPath: undefined }"
          category="campsites"
          class="cs-group-link"
        >
          <translate :tt="{ groupName: campsite.group.name }">
            View all {groupName} parks.
          </translate>
        </pu-search-link>
      </template>
    </template>

    <lazy-campsite-no-description v-else :campsite-slug="campsite.slug" />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import type {
  CampsiteDetails,
  CampsiteOpeningDateRange,
} from '../../../campsite-details/types'

@Component
class CampsiteDescription extends Vue {
  @Prop({ required: true })
  campsite: CampsiteDetails

  imageModules = import.meta.glob('@/assets/aa-pennant-rating/*.png', {
    query: '?url',
    eager: true,
  })

  get openNow(): boolean {
    if (this.campsite.openAllYear) {
      return true
    } else if (
      this.campsite.openingDateRanges &&
      this.campsite.openingDateRanges.length > 0
    ) {
      const openingDate = new Date(
        this.campsite.openingDateRanges[0].openingDate,
      )
      const closingDate = new Date(
        this.campsite.openingDateRanges[0].closingDate,
      )
      const now = new Date()
      return openingDate < now && now < closingDate
    }
    return false
  }

  isOpenAndCloseSameYear(dates: CampsiteOpeningDateRange) {
    const openingDate = new Date(dates.openingDate)
    const closingDate = new Date(dates.closingDate)
    return openingDate.getFullYear() === closingDate.getFullYear()
  }
}

export default toNative(CampsiteDescription)
</script>

<style scoped>
.site-details.top {
  display: flex;
  justify-content: space-between;
}

.site-details.top > div {
  flex: 0 1 auto;
}

.site-details.bottom {
  column-gap: 1.25em;
  display: grid;
  margin-bottom: 0.75em;
  grid-template-columns: minmax(max-content, 1fr) auto;
}

.site-details.bottom > div {
  margin: 0 0 0.75em 0;
  display: block;
  break-inside: avoid;
  -webkit-column-break-inside: avoid;
}

.tourist-board {
  margin-top: 0.75em;
}

.tourist-board-stars {
  white-space: nowrap;
}

.site-details p {
  font-size: var(--body);
  color: var(--colour-text-default);
}

.opening-dates p {
  font-size: var(--body);
}

.opening-dates p + p {
  margin-top: 0.5em;
}

.star-icon {
  width: 1.25em;
  margin-right: 0.1em;
}

.tick-icon {
  width: 1em;
}

.languages-spoken p {
  margin: 0;
}

.language-spoken {
  padding: 0.2em 0 0 0;
}

.site-details .open-status {
  color: var(--colour-orange);
  font-size: var(--body);
  margin-bottom: 0.25em;
}

.campsite-description {
  font-size: var(--body);
  margin: 1em 0 1.4em;
}

.cs-group-link {
  margin-top: 1em;
  font-size: var(--body);
  text-align: center;
  display: block;
}

.site-awards {
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-evenly;
  margin-top: 1em;
  width: 100%;
}

.site-awards img {
  width: auto;
  height: auto;
  max-width: 35vw;
  max-height: 35vw;
}
</style>
